import React, { useState, useEffect } from 'react';
import { Table, Button, Typography, notification } from 'antd';
import CustomInputField from '../../atoms/CustomInputField';
import { Radio } from 'antd';
import { ReloadOutlined, DollarOutlined, DeleteOutlined } from '@ant-design/icons';
import CustomModal from '../../atoms/CustomModal';
import MakePaymentForm from '../MakePaymentForm/MakePaymentForm';
import SubmitAndPrintForm from '../SubmitAndPrintForm/SubmitAndPrintForm';

const { Text } = Typography;

const SalesLeftTableSection = ({ selectedProducts = [], onProductDeselect, warehouseId }) => {
  ///// Local State
  const [recordList, setRecordList] = useState([]);
  const [taxPercentage, setTaxPercentage] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountType, setDiscountType] = useState('None');
  const [originalQuantities, setOriginalQuantities] = useState({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);

  useEffect(() => {
    // Initialize recordList and originalQuantities when selectedProducts change
    const initialRecords = selectedProducts.map((product) => ({
      ...product,
      quantity: product.quantity || 1,
    }));

    const initialQuantities = selectedProducts.reduce((acc, product) => {
      acc[product.warehouseStockID] = product.quantity || 1;
      return acc;
    }, {});

    setRecordList(initialRecords);
    setOriginalQuantities(initialQuantities);
  }, [selectedProducts]);

  const handleQuantityChange = (index, delta) => {
    setRecordList((prevList) => {
      const newList = [...prevList];
      const updatedProduct = {
        ...newList[index],
        // quantity: newList[index].quantity + delta,
        quantity: Math.max(1, (newList[index].quantity || 1) + delta),
      };
      newList[index] = updatedProduct;
      return newList;
    });
  };

  const handleDelete = (index) => {
    const productID = recordList[index].warehouseStockID;
    setRecordList((prevList) => prevList.filter((_, i) => i !== index));
    onProductDeselect(productID);
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (text, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button size='small' onClick={() => handleQuantityChange(index, -1)} style={{ marginRight: '5px' }}>
            -
          </Button>
          <span>{record.quantity}</span>
          <Button size='small' onClick={() => handleQuantityChange(index, 1)} style={{ marginLeft: '5px' }}>
            +
          </Button>
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'productPrice',
      key: 'productPrice',
      render: (price) => `${price ? price.toFixed(2) : '0.00'}`,
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      // render: (price) => `$ ${price.toFixed(2)}`,
    },
    {
      title: 'Sub Total',
      dataIndex: 'subTotal',
      key: 'subTotal',
      render: (text, record) => `${(record.quantity * (record.productPrice || 0)).toFixed(2)}`,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record, index) => (
        <Button type='link' onClick={() => handleDelete(index)} style={{ color: 'red' }}>
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  const totalQuantity = recordList.reduce((acc, record) => acc + (record.quantity || 1), 0);
  const subTotal = recordList.reduce((acc, record) => acc + (record.quantity || 1) * (record.productPrice || 0), 0);
  const taxAmount = (subTotal * taxPercentage) / 100;
  const discountAmount = discountType === 'Percentage' ? (subTotal * discountValue) / 100 : Number(discountValue);
  const total = subTotal + taxAmount - discountAmount;

  const dataSource = recordList.map((product, index) => ({
    key: index,
    productName: product.productName,
    quantity: product.quantity || 1,
    productPrice: product.productPrice || 0,
    subTotal: (product.quantity || 1) * (product.productPrice || 0),
    ...product,
  }));

  const handleTaxChange = (e) => {
    setTaxPercentage(e.target.value);
  };

  const handleDiscountChange = (e) => {
    setDiscountValue(e.target.value);
  };

  const handleDiscountTypeChange = (e) => {
    setDiscountType(e.target.value);
  };

  const resetQuantities = () => {
    setRecordList((prevList) =>
      prevList.map((product) => ({
        ...product,
        quantity: originalQuantities[product.warehouseStockID] || product.quantity || 1,
      })),
    );
  };

  const handlePaymentModalClose = () => {
    setPaymentModalOpen(false);
  };

  const afterCloseSubmitAndPrintModal = () => {
    setPrintModalOpen(false);
  };

  return (
    <>
      <div style={{ height: 'calc(100vh - 320px)', overflow: 'auto' }}>
        <Table columns={columns} dataSource={dataSource} pagination={false} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <CustomInputField
            type='text'
            placeholder='Tax %'
            size='large'
            value={taxPercentage}
            onChange={handleTaxChange}
          />
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ fontWeight: '600' }}>Discount</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Radio.Group onChange={handleDiscountTypeChange} value={discountType}>
                <Radio value='Fixed'>Fixed</Radio>
                <Radio value='Percentage'>Percentage</Radio>
                <Radio value='None'>None</Radio>
              </Radio.Group>
            </div>
          </div>
          <CustomInputField
            type='text'
            placeholder='Discount'
            size='large'
            value={discountValue}
            onChange={handleDiscountChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text type='secondary'>Total Quantity: {totalQuantity}</Text>
          <Text type='secondary'>Total Tax: {taxAmount}</Text>
          <Text type='secondary'>Sub Total: $ {subTotal ? subTotal.toFixed(2) : '0.00'}</Text>
          <Text strong>Total: $ {total ? total.toFixed(2) : '0.00'}</Text>
        </div>
      </div>
      <div style={{ padding: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <Button type='primary' danger size='large' icon={<ReloadOutlined />} onClick={resetQuantities}>
          Reset
        </Button>
        <Button
          type='button'
          style={{ backgroundColor: 'rgb(10, 192, 116)', color: 'white' }}
          size='large'
          icon={<DollarOutlined />}
          onClick={() => {
            if (recordList.length > 0) {
              setPaymentModalOpen(true);
            } else {
              notification.error({
                message: 'Something went wrong',
                description: 'Please add product to cart.',
                placement: 'topRight',
              });
            }
          }}
        >
          Pay Now
        </Button>
      </div>
      {paymentModalOpen && (
        <CustomModal
          title={`Make Payment`}
          okText='Save'
          modalOpen={paymentModalOpen}
          afterCloseModal={handlePaymentModalClose}
          open={paymentModalOpen}
          onOk={handlePaymentModalClose}
          onCancel={handlePaymentModalClose}
          footer={null}
          width={1200}
        >
          <MakePaymentForm
            subTotal={subTotal}
            total={total}
            taxAmount={taxAmount}
            discountAmount={discountAmount}
            recordList={recordList}
            discountType={discountType}
            handlePaymentModalClose={handlePaymentModalClose}
            setPrintModalOpen={setPrintModalOpen}
            setRecordList={setRecordList}
            warehouseId={warehouseId}
          />
        </CustomModal>
      )}

      {printModalOpen && (
        <CustomModal
          title={`Invoice POS`}
          okText='Print'
          modalOpen={printModalOpen}
          afterCloseModal={afterCloseSubmitAndPrintModal}
          open={printModalOpen}
          onOk={afterCloseSubmitAndPrintModal}
          onCancel={afterCloseSubmitAndPrintModal}
          footer={null}
        >
          <SubmitAndPrintForm recordList={recordList} />
        </CustomModal>
      )}
    </>
  );
};

export default SalesLeftTableSection;
